import privacyBody from "./content/privacy.md?raw";
import termsBody from "./content/terms.md?raw";

/**
 * Public legal surfaces (`/privacy`, `/terms`).
 *
 * The prose lives in `content/*.md` and is inlined at build time via Vite's
 * `?raw` import, so updating the policy is a markdown edit — no TSX, no route
 * change. It is NOT the help-docs pipeline (`docs/help/` →
 * `scripts/sync-help-docs.mjs` → `help-content.generated.ts`): help articles
 * are audience-scoped and sit behind the `/docs` auth gate, whereas these two
 * must be readable by anyone, signed out, on every host kind. They share only
 * the `DocsMarkdown` renderer.
 *
 * Because the bodies are baked into the bundle, publishing new text requires a
 * deploy. That is the right trade for documents that change rarely and must be
 * versioned alongside the code that they describe.
 *
 * CONTENT RULE: everything in `content/*.md` is shown verbatim to the public.
 * `DocsMarkdown` renders with raw HTML disabled, so an HTML comment is NOT
 * stripped — it reaches the reader as literal text. Drafting instructions and
 * anything else internal belong in `docs/LEGAL_DRAFTING_NOTES.md`; a test
 * asserts these files contain no `<!--`.
 */
export type LegalDocId = "privacy" | "terms";

export interface LegalDoc {
  id: LegalDocId;
  /** Route path — also the canonical public URL for the document. */
  path: string;
  /** `<h1>` is in the markdown; this is the tab/OG title. */
  title: string;
  description: string;
  body: string;
}

export const LEGAL_DOCS: Record<LegalDocId, LegalDoc> = {
  privacy: {
    id: "privacy",
    path: "/privacy",
    // Retitled in rollout Phase 3: the page carries the beta privacy
    // notice that is in force, not a "Policy" placeholder. Counsel's
    // launch-grade policy will take this slot later.
    title: "Privacy Notice · tktic",
    description:
      "How tktic handles personal data during the closed beta.",
    body: privacyBody,
  },
  terms: {
    id: "terms",
    path: "/terms",
    title: "Terms of Service · tktic",
    description: "The terms that govern use of tktic.",
    body: termsBody,
  },
};

/**
 * True while a document still carries its unreviewed-draft marker. The marker
 * is the blockquote at the top of the markdown, so the flag is derived from the
 * content rather than tracked separately — there is no way to publish the text
 * and leave a stale "reviewed" boolean behind, or vice versa.
 *
 * Nothing gates on this today; it exists so a test can assert that shipping a
 * draft is a deliberate act, and so we can surface a banner if these pages go
 * live before counsel signs off.
 */
export function isDraft(doc: LegalDoc): boolean {
  return doc.body.includes("DRAFT — NOT YET IN FORCE");
}
